<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const sdgGoals = [
  { num: 1, name: 'No Poverty', icon: '🏠', selected: false },
  { num: 2, name: 'Zero Hunger', icon: '🌾', selected: false },
  { num: 3, name: 'Good Health & Well-being', icon: '❤️', selected: true },
  { num: 4, name: 'Quality Education', icon: '📚', selected: false },
  { num: 5, name: 'Gender Equality', icon: '⚖️', selected: false },
  { num: 6, name: 'Clean Water & Sanitation', icon: '💧', selected: true },
  { num: 7, name: 'Affordable & Clean Energy', icon: '⚡', selected: true },
  { num: 8, name: 'Decent Work & Economic Growth', icon: '💼', selected: true },
  { num: 13, name: 'Climate Action', icon: '🌍', selected: true },
  { num: 15, name: 'Life on Land', icon: '🌿', selected: true },
]
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">SDG Co-benefits Reporting</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">SDG Co-benefits Reporting</h1>
            <p class="text-gray-600">
              Article 6.4 projects are required to demonstrate and report on contributions to the UN Sustainable Development Goals (SDGs) beyond greenhouse gas reductions. Report your project's social, economic, and environmental co-benefits annually.
            </p>
          </div>
        </section>

        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Solar Park Beta – Northern Highlands</option>
                  <option>Mangrove Restoration Initiative – Borneo</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Reporting Period *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>2024 (Annual)</option>
                  <option>2025 (Annual)</option>
                </select>
              </div>
            </div>
          </section>

          <!-- SDG selection -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-2 flex items-center">
              <i class="fas fa-globe text-primary mr-3"></i>SDG Contributions
            </h2>
            <p class="text-sm text-gray-600 mb-4">Select all SDGs your project contributes to, then describe the co-benefit for each.</p>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <div
                v-for="goal in sdgGoals"
                :key="goal.num"
                :class="[
                  'border-2 rounded-lg p-3 text-center cursor-pointer transition-colors',
                  goal.selected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <div class="text-2xl mb-1">{{ goal.icon }}</div>
                <p class="text-xs font-medium text-gray-700">SDG {{ goal.num }}</p>
                <p class="text-xs text-gray-500 leading-tight">{{ goal.name }}</p>
              </div>
            </div>
          </section>

          <!-- Co-benefit details -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-4 flex items-center">
              <i class="fas fa-list text-primary mr-3"></i>Co-benefit Descriptions
            </h2>
            <div class="space-y-4">
              <div v-for="goal in sdgGoals.filter(g => g.selected)" :key="goal.num" class="border border-gray-200 rounded-lg p-4">
                <h3 class="font-medium text-accent mb-2">{{ goal.icon }} SDG {{ goal.num }}: {{ goal.name }}</h3>
                <textarea rows="3" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" :placeholder="`Describe how this project contributes to SDG ${goal.num}.`"></textarea>
                <div class="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Quantitative Indicator (if applicable)</label>
                    <input type="text" class="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. 500 jobs created" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Evidence / Source</label>
                    <input type="text" class="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. Community survey report" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-4 flex items-center">
              <i class="fas fa-upload text-primary mr-3"></i>Supporting Documentation
            </h2>
            <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
              <p class="text-gray-600 mb-1">Upload evidence of co-benefits</p>
              <p class="text-xs text-gray-500">Community survey, employment records, environmental assessments (PDF, DOCX up to 10MB)</p>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                <i class="fas fa-save mr-2"></i>Save Draft
              </button>
              <button type="submit" class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90">
                <i class="fas fa-paper-plane mr-2"></i>Submit SDG Report
              </button>
            </div>
          </section>
        </form>

      </div>
    </main>
  </div>
</template>
